import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ResourceError } from "../../utils/ResourceError";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { SaveBar } from "../../utils/SaveBar";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import { fetchHseSettings, updateHseSettings } from "../../../api/hse/settings";
import { HseFieldInfoLabel } from "./HseFieldInfoLabel";
import {
  normalizeHseSafetyRepSettings,
  snapshotHseSafetyRepSettings,
  toHseSafetyRepPayload,
} from "./hseCompanySettingsUtils";

export function HseSafetyRepSettingsForm({ onSaved }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const rememberUser = (event) => {
    const user = event.target.object;
    if (user?.id) setSelectedUsers((previous) => [...previous.filter((row) => row.id !== user.id), user]);
  };
  const selectedUser = (id) => id ? selectedUsers.find((user) => user.id === id) || { id, displayTitle: `#${id}` } : null;
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [settings, setSettings] = useState(normalizeHseSafetyRepSettings({}));
  const [savedSnapshot, setSavedSnapshot] = useState(
    snapshotHseSafetyRepSettings({})
  );

  const applyLoaded = useCallback((data) => {
    setSelectedUsers(data.selected_users || []);
    const next = normalizeHseSafetyRepSettings(data);
    setSettings(next);
    setSavedSnapshot(snapshotHseSafetyRepSettings(next));
    onSaved?.(data);
  }, [onSaved]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchHseSettings();
        if (!cancelled) applyLoaded(data);
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyLoaded, t]);

  const hasChanges = useMemo(
    () => snapshotHseSafetyRepSettings(settings) !== savedSnapshot,
    [settings, savedSnapshot]
  );

  const patch = (next) => {
    setSaveSuccess(false);
    setSaveError(false);
    setSettings((prev) => ({ ...prev, ...next }));
  };

  const addDeputy = (userId) => {
    if (!userId) return;
    if (settings.deputy_user_ids.includes(userId)) return;
    if (userId === settings.safety_representative_user_id) return;
    patch({ deputy_user_ids: [...settings.deputy_user_ids, userId] });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(false);
    try {
      const data = await updateHseSettings(toHseSafetyRepPayload(settings));
      applyLoaded(data);
      setSaveSuccess(true);
      return true;
    } catch {
      setSaveError(true);
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ResourceLoading />;

  if (loadError) return <ResourceError errors={[t("hse.settings.load_failed")]} />;

  return (
    <>
      <fieldset disabled={saving} className="mx-auto min-w-0 max-w-3xl space-y-4 pb-28">
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4">
            <HseFieldInfoLabel
              label="hse.settings.safety_rep.title"
              infoTitle="hse.settings.safety_rep.info_title"
              infoBody="hse.settings.safety_rep.info_body"
            />
            <p className="mt-1 text-sm text-slate-600">
              {t("hse.settings.safety_rep.description")}
            </p>
          </div>
          <EmployeeSelector
            name="safety_representative_user_id"
            label="hse.settings.safety_rep.who"
            values={{ safety_representative_user_id: settings.safety_representative_user_id }}
            setFieldValue={(_, value) =>
              patch({ safety_representative_user_id: value || null })
            }
            selected={selectedUser(settings.safety_representative_user_id)}
            onChange={rememberUser}
            key={settings.safety_representative_user_id || "none"}
          />
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h3 className="text-base font-semibold text-slate-900">
            {t("hse.settings.safety_rep.deputies_title")}
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            {t("hse.settings.safety_rep.deputies_description")}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {settings.deputy_user_ids.length === 0 ? (
              <p className="text-sm text-slate-500">{t("hse.settings.safety_rep.deputies_empty")}</p>
            ) : (
              settings.deputy_user_ids.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-800"
                >
                  {selectedUser(id)?.displayTitle}
                  <button
                    type="button"
                    onClick={() =>
                      patch({
                        deputy_user_ids: settings.deputy_user_ids.filter((item) => item !== id),
                      })
                    }
                    className="rounded-full p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                    aria-label={t("hse.settings.safety_rep.remove_deputy")}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </span>
              ))
            )}
          </div>
          <div className="mt-4">
            <EmployeeSelector
              name="deputy_user_id"
              label="hse.settings.safety_rep.add_deputy"
              values={{ deputy_user_id: null }}
              excludeUserIds={[
                settings.safety_representative_user_id,
                ...settings.deputy_user_ids,
              ]}
              setFieldValue={(_, value) => addDeputy(value)}
              selected={null}
              key={settings.deputy_user_ids.join(",")}
              onChange={rememberUser}
            />
          </div>
        </section>
      </fieldset>

      <SaveBar
        hasChanges={hasChanges}
        isSaving={saving}
        isSuccess={saveSuccess}
        isError={saveError}
        successMessage={t("hse.settings.saved")}
        errorMessage={t("hse.settings.save_failed")}
        onSave={handleSave}
        onDismiss={() => setSaveError(false)}
        saveLabel={t("save")}
      />
    </>
  );
}
